import { formatArticleDate } from '@archetelos/lib/date';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { useAuth } from '@/lib/auth/useAuth';
import { contentHref } from '@/lib/contentHref';
import { subscriberQueries } from '@/lib/queries/subscriber';

export function HistoryView({ kind }: { kind: 'article' | 'video' }) {
  const { user } = useAuth();
  const isVideo = kind === 'video';
  // Split at Spec 33: the watching tab reads watch_progress; the reading tab is
  // unchanged. Both hooks run; the inactive one is disabled via an undefined id.
  const reading = useQuery(
    subscriberQueries.readingHistory(isVideo ? undefined : user?.id, 'article'),
  );
  const watching = useQuery(subscriberQueries.watchHistory(isVideo ? user?.id : undefined));
  const active = isVideo ? watching : reading;
  const label = isVideo ? 'watching' : 'reading';

  if (active.isLoading) {
    return <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>;
  }

  // Normalize to one row shape. The article status string is identical to the
  // pre-Spec-33 render (byte-identical reading tab). The video % label is
  // dormant in Increment 1 (duration null → "Watched") and lights up in
  // Increment 2 when position/duration are fed.
  const items = isVideo
    ? (watching.data ?? []).map((h) => ({
        contentId: h.content_id,
        title: h.content?.title ?? 'Untitled',
        slug: h.content?.slug ?? '',
        completed: h.completed,
        dateISO: h.last_watched_at,
        status: h.completed
          ? 'Finished'
          : h.duration_seconds && h.duration_seconds > 0
            ? `${Math.round((h.position_seconds / h.duration_seconds) * 100)}% watched`
            : 'Watched',
      }))
    : (reading.data ?? []).map((h) => ({
        contentId: h.content_id,
        title: h.content?.title ?? 'Untitled',
        slug: h.content?.slug ?? '',
        completed: h.completed,
        dateISO: h.last_read_at,
        status: h.completed ? 'Finished' : `${h.scroll_percent}% read`,
      }));

  if (items.length === 0) {
    return (
      <p className="mt-8 text-center font-sans text-sm text-ink/50">No {label} history yet.</p>
    );
  }

  return (
    <ul className="mt-6 divide-y divide-ink/10">
      {items.map((h) => (
        <li key={h.contentId} className="flex items-center gap-4 py-4">
          <div className="min-w-0 flex-1">
            <Link
              to={contentHref(kind, h.slug)}
              className="font-serif text-base text-ink hover:text-oxblood"
            >
              {h.title}
            </Link>
            <p className="font-sans text-xs text-ink/50">
              {h.status} · {formatArticleDate(new Date(h.dateISO))}
            </p>
          </div>
          {!h.completed && (
            <span className="shrink-0 rounded-sm bg-oxblood/10 px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] text-oxblood">
              Resume
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
